import { shuffle, sample } from "../util";

const quizModes = ["easy", "normal", "hard", "expert"] as const;

const quizResultGrades = [
  "short",
  "tall",
  "grande",
  "trenta",
  "master",
] as const;

export type QuizScene = "mode-select" | "playing" | "answers";
export type QuizMode = typeof quizModes[number];
export type QuizResultGrade = typeof quizResultGrades[number];
export type QuizLevel = "easy" | "normal" | "hard";

export function isQuizMode(x: string): x is QuizMode {
  return quizModes.some((mode) => String(mode) === x);
}

export function isQuizResultGrade(x: string): x is QuizResultGrade {
  return quizResultGrades.some((grade) => String(grade) === x);
}

export interface QuizChoice {
  text: string;
  isCorrect: boolean;
}

export interface QuizQuestion {
  level: QuizLevel;
  question: string;
  explanation: string;
  related_urls: Array<string>;
  choices: Array<QuizChoice>;
}

export interface QuizCollection {
  name: string;
  description: string;
  quizzes: Array<QuizQuestion>;
}

interface Answer {
  quizMode: QuizMode;
  questionIndex: number;
  choiceIndex: number;
}

interface Result {
  numCorrects: number;
  numQuestions: number;
  grade: QuizResultGrade;
}

export class QuizHandler {
  private questions: Array<QuizQuestion>;
  private currentMode: QuizMode | null = null;
  private currentQuestionIndices: Array<number> | null = null;
  private answers: Array<Answer> | null = null;

  constructor() {
    this.questions = require("./quizzes.json");
  }

  public getQuestionIndices(mode: QuizMode): Array<number> {
    /*
     * クイズのモードごとにランダムに問題をサンプリングして返します。
     * モードごとのクイズの振り分けルールについてはこちらをご確認ください:
     * https://github.com/akikunsuki-com/birthday2021/issues/43#issuecomment-974656279
     */
    switch (mode) {
      case "easy":
        return sample(
          this.questions
            .map((_, i) => i)
            .filter((i) => this.questions[i].level === "easy"),
          10
        );
      case "normal":
        return shuffle(
          sample(
            this.questions
              .map((_, i) => i)
              .filter((i) => this.questions[i].level === "easy"),
            5
          ).concat(
            sample(
              this.questions
                .map((_, i) => i)
                .filter((i) => this.questions[i].level === "normal"),
              15
            )
          )
        );
      case "hard":
        return shuffle(
          sample(
            this.questions
              .map((_, i) => i)
              .filter((i) => this.questions[i].level === "normal"),
            15
          ).concat(
            sample(
              this.questions
                .map((_, i) => i)
                .filter((i) => this.questions[i].level === "hard"),
              5
            )
          )
        );
      case "expert":
        return shuffle(
          sample(
            this.questions
              .map((_, i) => i)
              .filter((i) => this.questions[i].level === "normal"),
            15
          ).concat(
            sample(
              this.questions
                .map((_, i) => i)
                .filter((i) => this.questions[i].level === "hard"),
              15
            )
          )
        );
      default:
        throw new Error(`invalid quiz mode ${mode}`);
    }
  }

  public getQuestion(index: number): QuizQuestion {
    const question = this.questions[index];
    if (question === undefined) {
      throw new Error(`Invalid question index: ${index}`);
    }
    return question;
  }

  public getChoice(questionIndex: number, choiceIndex: number): QuizChoice {
    const question = this.getQuestion(questionIndex);
    const choice = question.choices[choiceIndex];
    if (choice === undefined) {
      throw new Error("Invalid choice index " + choiceIndex);
    }
    return choice;
  }

  public startQuiz(mode: QuizMode) {
    this.currentMode = mode;
    this.currentQuestionIndices = this.getQuestionIndices(mode);
    this.answers = [];
  }

  public getCurrentMode(): QuizMode {
    if (this.currentMode === null) {
      throw new Error("Quiz was not started.");
    }
    return this.currentMode;
  }

  public getCurrentQuestionIndices(): Array<number> {
    if (this.currentQuestionIndices === null) {
      throw new Error("Quiz was not started.");
    }
    return this.currentQuestionIndices;
  }

  public getNumCurrentQuestions(): number {
    const questionIndices = this.getCurrentQuestionIndices();
    return questionIndices.length;
  }

  public getAnswers(): Array<Answer> {
    if (this.answers === null) {
      throw new Error("Quiz was not started.");
    }
    return this.answers;
  }

  public addAnswer(answer: Answer) {
    const mode = this.getCurrentMode();

    // validate indies
    if (mode !== answer.quizMode) {
      throw new Error(
        "Answer's quiz mode must be equal to the current quiz mode"
      );
    }
    this.getChoice(answer.questionIndex, answer.choiceIndex);

    const answers = this.getAnswers().filter(
      // remove duplicated question index
      (x) => x.questionIndex !== answer.questionIndex
    );
    answers.push(answer);
    this.answers = answers;
  }

  public computeNumCorrects(): number {
    let score = 0;

    const answers = this.getAnswers();
    for (const answer of answers) {
      const choice = this.getChoice(answer.questionIndex, answer.choiceIndex);
      if (choice.isCorrect) {
        score += 1;
      }
    }

    return score;
  }

  public getGradeFromScore(score: number): QuizResultGrade {
    if (this.currentMode === null) {
      throw new Error("Quiz was not started.");
    }

    const registry = quizResultRegistry[this.currentMode];

    let grade: QuizResultGrade = "short";
    for (const g of Object.keys(registry) as QuizResultGrade[]) {
      if (
        score >= registry[g]!.lowerCorrects &&
        registry[g]!.lowerCorrects > registry[grade]!.lowerCorrects
      ) {
        grade = g;
      }
    }

    return grade;
  }

  public getResult(): Result {
    const numCorrects = this.computeNumCorrects();
    const numQuestions = this.getNumCurrentQuestions();
    const grade = this.getGradeFromScore(numCorrects);
    return {
      numCorrects: numCorrects,
      numQuestions: numQuestions,
      grade: grade,
    };
  }
}

type QuizResultGradeRegistry = {
  [key in QuizMode]: {
    [key in QuizResultGrade]?: {
      lowerCorrects: number;
      imagePath: string;
    };
  };
};

export const quizResultRegistry: QuizResultGradeRegistry = {
  easy: {
    short: {
      lowerCorrects: 0,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/easy_short.png`,
    },
    tall: {
      lowerCorrects: 7,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/easy_tall.png`,
    },
  },
  normal: {
    short: {
      lowerCorrects: 0,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/normal_short.png`,
    },
    tall: {
      lowerCorrects: 7,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/normal_tall.png`,
    },
    grande: {
      lowerCorrects: 16,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/normal_grande.png`,
    },
  },
  hard: {
    short: {
      lowerCorrects: 0,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/hard_short.png`,
    },
    tall: {
      lowerCorrects: 7,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/hard_tall.png`,
    },
    grande: {
      lowerCorrects: 12,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/hard_grande.png`,
    },
    trenta: {
      lowerCorrects: 18,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/hard_trenta.png`,
    },
  },
  expert: {
    short: {
      lowerCorrects: 0,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/expert_short.png`,
    },
    tall: {
      lowerCorrects: 7,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/expert_tall.png`,
    },
    grande: {
      lowerCorrects: 16,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/expert_grande.png`,
    },
    trenta: {
      lowerCorrects: 25,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/expert_trenta.png`,
    },
    master: {
      lowerCorrects: 30,
      imagePath: `${process.env.PUBLIC_URL}/img/quizzes/expert_master.png`,
    },
  },
};

export function encodeQuizResult(
  mode: QuizMode,
  grade: QuizResultGrade
): string {
  const key = "akikunsuki";
  const plain = `${mode}:${grade}`;
  const secret = Buffer.from(xorString(plain, key)).toString("base64");
  return secret;
}

export function decodeQuizResult(encoded: string): {
  mode: QuizMode;
  grade: QuizResultGrade;
} {
  const key = "akikunsuki";
  const plain = xorString(Buffer.from(encoded, "base64").toString(), key);
  const plains = plain.split(":");

  if (!isQuizMode(plains[0])) {
    throw new Error(`Invalid quiz mode: ${plains[0]}`);
  }

  if (!isQuizResultGrade(plains[1])) {
    throw new Error(`Invalid quiz result grade: ${plains[1]}`);
  }

  const mode = plains[0];
  const grade = plains[1];

  return { mode, grade };
}

function xorString(a: string, b: string): string {
  while (a.length > b.length) {
    b += b;
  }

  let xor = "";
  for (let i = 0; i < a.length; i++) {
    const ai = a[i].charCodeAt(0);
    const bi = b[i].charCodeAt(0);
    xor += String.fromCharCode(ai ^ bi);
  }

  return xor;
}

/* For debugging
  console.log("easy short", encodeQuizResult("easy", "short"));
  console.log("easy tall", encodeQuizResult("easy", "tall"));
  console.log("normal short", encodeQuizResult("normal", "short"));
  console.log("normal tall", encodeQuizResult("normal", "tall"));
  console.log("normal grande", encodeQuizResult("normal", "grande"));
  console.log("hard short", encodeQuizResult("hard", "short"));
  console.log("hard tall", encodeQuizResult("hard", "tall"));
  console.log("hard grande", encodeQuizResult("hard", "grande"));
  console.log("hard trenta", encodeQuizResult("hard", "trenta"));
  console.log("expert short", encodeQuizResult("expert", "short"));
  console.log("expert tall", encodeQuizResult("expert", "tall"));
  console.log("expert grande", encodeQuizResult("expert", "grande"));
  console.log("expert trenta", encodeQuizResult("expert", "trenta"));
  console.log("expert master", encodeQuizResult("expert", "master"));
*/
